import React, { useEffect, useMemo } from "react";
import {
  FaHome,
  FaBuilding,
  FaClock,
  FaRoad,
  FaChartBar,
  FaUsers,
  FaCreditCard,
  FaQuestionCircle,
} from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";
import { all_routes } from "../feature-module/router/all_routes";
import { useDispatch, useSelector } from "react-redux";
import { setIsRealEstate } from "../core/data/redux/user/userSlice";
import HomeBlackIcon from "../icons/HomeBlackIcon";
import PropertyBlackIcon from "../icons/PropertyBlackIcon";
import LogHoursBlackIcon from "../icons/LogHoursBlackIcon";
import MileageBlackIcon from "../icons/MileageBlackIcon";
import AuditTrailIcon from "../icons/AuditTrailIcon";
import ReportsBlackIcon from "../icons/ReportsBlackIcon";
import ManageUserBlackIcon from "../icons/ManageUserBlackIcon";
import BillingBlackIcon from "../icons/BillingBlackIcon";
import HelpBlackIcon from "../icons/HelpBlackIcon";
import ActivitiesIcon from "../icons/ActivitiesIcon";
import LogHoursIcon from "../icons/LogHoursIcon";
import CustomerSupportIcon from "../icons/CustomerSupportIcon";


interface SidebarProps {
  isOpen: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen }) => {
  const routes: any = all_routes;
  const location = useLocation();
  const dispatch = useDispatch();
  const userDetail = useSelector((state: any) => state.user?.userDetail);
  const isRealEstate = useSelector((state: any) => state.user.isRealEstate);
  const planPermission = useSelector((state: any) => state.user?.planPermission);

  const userRole = userDetail?.role?.toLowerCase() || "owner";

  // const planPermission = userDetail?.plan_permissions;
  // const contactSupportPermission = planPermission?.permissions?.contact_support;

  const contactSupportPermission = useMemo(() => {
    return planPermission?.permissions?.contact_support;
  }, [planPermission?.plan_name]);


  // Listen for storage changes to update isRealEstate in Redux
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "isRealEstate") {
        const newValue = event.newValue !== null ? JSON.parse(event.newValue) : true;
        dispatch(setIsRealEstate(newValue));
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [dispatch]);

  const isActive = (path: string) => {
    return location.pathname.startsWith(path) ? "active" : "";
  };

  return (
    <aside className={`sidebar ${isOpen ? "expanded" : "collapsed"}`}>
      {(userRole == 'owner') && isRealEstate && (
        <ul>
          <li>
            <Link to={routes.dashboard} className={isActive(routes.dashboard)}>
              <HomeBlackIcon />
              <span>Dashboard</span>
            </Link>
          </li>
          <li>
            <Link to={routes.properties} className={isActive(routes.properties)}>
              <PropertyBlackIcon />
              <span>Property</span>
            </Link>
          </li>
          <li>
            <Link to={routes.logHours} className={isActive(routes.logHours)}>
              <LogHoursBlackIcon />
              <span>Log Hours</span>
            </Link>
          </li>
          <li>
            <Link to={routes.mileage} className={isActive(routes.mileage)}>
              <MileageBlackIcon />
              <span>Mileage</span>
            </Link>
          </li>
          <li>
            <Link to={routes.auditTrail} className={isActive(routes.auditTrail)}>
              <AuditTrailIcon />
              <span>Audit Trail</span>
            </Link>
          </li>
          <li>
            <Link to={routes.reports} className={isActive(routes.reports)}>
              <ReportsBlackIcon />
              <span>Reports</span>
            </Link>
          </li>
          <li>
            <Link to={routes.manageUser} className={isActive(routes.manageUser)}>
              <ManageUserBlackIcon />
              <span>Manage User</span>
            </Link>
          </li>
          <li>
            <Link to={routes.billingAndSubscription} className={isActive(routes.billingAndSubscription) || isActive(routes.planList)}>
              <BillingBlackIcon />
              <span>Billing & Subscription</span>
            </Link>
          </li>
          {contactSupportPermission && (
            <li>
              <Link to={routes.customerSupport} className={isActive(routes.customerSupport)}>
                <CustomerSupportIcon />
                <span>Contact Support</span>
              </Link>
            </li>
          )}
          <li>
            <Link to={routes.helpCenter} className={isActive(routes.helpCenter)}>
              <HelpBlackIcon />
              <span>Help Center</span>
            </Link>
          </li>
        </ul>
      )}

      {(userRole == 'owner') && (!isRealEstate) && (
        <ul>
          <li>
            <Link to={routes.nonRealDashboard} className={isActive(routes.nonRealDashboard)}>
              <HomeBlackIcon />
              <span>Dashboard</span>
            </Link>
          </li>
          <li>
            <Link to={routes.nonRealActivity} className={isActive(routes.nonRealActivity)}>
              <ActivitiesIcon />
              <span>Activities</span>
            </Link>
          </li>
          <li>
            <Link to={routes.nonRealLogHours} className={isActive(routes.nonRealLogHours)}>
              <LogHoursIcon />
              <span>Log Hours</span>
            </Link>
          </li>
          <li>
            <Link to={routes.nonRealReports} className={isActive(routes.nonRealReports)}>
              <ReportsBlackIcon />
              <span>Reports</span>
            </Link>
          </li>
          <li>
            <Link to={routes.nonRealManageUser} className={isActive(routes.nonRealManageUser)}>
              <ManageUserBlackIcon />
              <span>Manage User's</span>
            </Link>
          </li>
          <li>
            <Link to={routes.nonRealBillingAndSubscription} className={isActive(routes.nonRealBillingAndSubscription) || isActive(routes.planList)}>
              <BillingBlackIcon />
              <span>Billing & Subscription</span>
            </Link>
          </li>
          {contactSupportPermission && (
            <li>
              <Link to={routes.customerSupport} className={isActive(routes.customerSupport)}>
                <CustomerSupportIcon />
                <span>Contact Support</span>
              </Link>
            </li>
          )}
          <li>
            <Link to={routes.helpCenter} className={isActive(routes.helpCenter)}>
              <HelpBlackIcon />
              <span>Help Center</span>
            </Link>
          </li>
        </ul>
      )}

      {(userRole == 'spouse') && (
        <ul>
          <li>
            <Link to={routes.spouseDashboard} className={isActive(routes.spouseDashboard)}>
              <HomeBlackIcon />
              <span>Dashboard</span>
            </Link>
          </li>
          <li>
            <Link to={routes.spouseProperties} className={isActive(routes.spouseProperties)}>
              <PropertyBlackIcon />
              <span>Property</span>
            </Link>
          </li>
          <li>
            <Link to={routes.spouseLogHours} className={isActive(routes.spouseLogHours)}>
              <LogHoursIcon />
              <span>Log Hours</span>
            </Link>
          </li>
          <li>
            <Link to={routes.spouseMileage} className={isActive(routes.spouseMileage)}>
              <ManageUserBlackIcon />
              <span>Mileage</span>
            </Link>
          </li>
          <li>
            <Link to={routes.spouseReports} className={isActive(routes.spouseReports)}>
              <ReportsBlackIcon />
              <span>Reports</span>
            </Link>
          </li>
        </ul>
      )}

      {(userRole == 'tax_professional') && (
        <ul>
          <li>
            <Link to={routes.cpaDashboard} className={isActive(routes.cpaDashboard)}>
              <FaHome />
              <span>Dashboard</span>
            </Link>
          </li>
          <li>
            <Link to={routes.cpaProperties} className={isActive(routes.cpaProperties)}>
              <PropertyBlackIcon />
              <span>Property</span>
            </Link>
          </li>
          <li>
            <Link to={routes.cpaLogHours} className={isActive(routes.cpaLogHours)}>
              <LogHoursIcon />
              <span>Log Hours</span>
            </Link>
          </li>
          <li>
            <Link to={routes.cpaMileage} className={isActive(routes.cpaMileage)}>
              <ManageUserBlackIcon />
              <span>Mileage</span>
            </Link>
          </li>
          <li>
            <Link to={routes.cpaAuditTrail} className={isActive(routes.cpaAuditTrail)}>
              <AuditTrailIcon />
              <span>Audit Trail</span>
            </Link>
          </li>
          <li>
            <Link to={routes.cpaReports} className={isActive(routes.cpaReports)}>
              <ReportsBlackIcon />
              <span>Reports</span>
            </Link>
          </li>
        </ul>
      )}

    </aside>
  );
};

export default Sidebar;
